<template>
  <v-dialog
    v-model="dialog"
    :max-width="!suggestion ? '600' : '920'"
    :persistent="loading || adapting"
    scrollable
    :fullscreen="$vuetify.breakpoint.smAndDown"
  >
    <v-card class="generate-artist-dialog">
      <v-card-title class="pb-1">
        Gerar artista com IA
        <v-spacer />
        <v-btn icon small :disabled="loading || adapting" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-2">
        <template v-if="!suggestion">
          <v-text-field
            v-model="name"
            label="Nome do artista"
            outlined
            dense
            hide-details="auto"
            :disabled="loading"
            hint="Buscamos dados, Spotify e fotos da web para você escolher"
            persistent-hint
            @keyup.enter="generate"
          />
          <v-btn
            color="primary"
            class="mt-4"
            block
            :loading="loading"
            :disabled="loading || !(name || '').trim()"
            @click="generate"
          >
            <v-icon left>mdi-auto-fix</v-icon>
            Gerar sugestão
          </v-btn>
        </template>

        <template v-else>
          <v-row dense class="mx-0">
            <v-col cols="12" md="">
              <div class="artist-summary rounded mb-4 pa-3">
                <div class="d-flex align-start flex-wrap" style="gap: 12px">
                  <div class="flex-grow-1" style="min-width: 200px">
                    <div class="text-h6 font-weight-bold mb-1">
                      {{ suggestion.name }}
                    </div>
                    <div
                      class="d-flex align-center flex-wrap mb-2"
                      style="gap: 8px"
                    >
                      <v-chip small label>{{ suggestion.category }}</v-chip>
                      <v-chip
                        v-if="suggestion.slug"
                        small
                        label
                        outlined
                        class="text--secondary"
                      >
                        {{ suggestion.slug }}
                      </v-chip>
                      <div class="d-flex align-center">
                        <v-avatar :color="suggestion.color" size="18" />
                        <span class="text-caption ml-1 text--secondary">
                          {{ suggestion.color }}
                        </span>
                      </div>
                    </div>
                    <span class="text-caption font-weight-bold mt-2">
                      Descrição curta:
                    </span>
                    <p class="text-body-2 mb-0 summary-bio ml-2">
                      {{
                        suggestion.shortDescription || suggestion.description
                      }}
                    </p>
                    <template v-if="suggestion.description">
                      <span class="text-caption font-weight-bold mt-2">
                        Descrição longa:
                      </span>
                      <p class="text-body-2 mb-0 ml-2">
                        {{ suggestion.description }}
                      </p>
                    </template>
                  </div>

                  <div
                    v-if="suggestion.spotifyId"
                    class="spotify-mini d-flex align-center"
                  >
                    <v-icon small color="#1DB954" class="mr-1">
                      mdi-spotify
                    </v-icon>
                    <span class="text-caption">Spotify encontrado</span>
                  </div>
                </div>
              </div>
            </v-col>
            <v-col v-if="suggestion.spotifyId" cols="12" md="5">
              <!-- spotify embed player -->
              <div class="spotify-embed-player">
                <iframe
                  :src="`https://open.spotify.com/embed/${suggestion.spotifyId}`"
                  width="100%"
                  height="360"
                  frameborder="0"
                  allowtransparency="true"
                  allow="encrypted-media"
                ></iframe>
              </div>
            </v-col>
          </v-row>

          <div class="text-subtitle-2 mb-2">Escolher imagens</div>
          <v-tabs
            v-model="imageTab"
            background-color="transparent"
            height="40"
            class="mb-3"
            :disabled="adapting"
          >
            <v-tab v-for="tab in imageTabs" :key="tab.key" class="text-none">
              {{ tab.label }}
              <v-chip x-small class="ml-2" label>
                {{ tab.count }}
              </v-chip>
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="imageTab">
            <v-tab-item v-for="tab in imageTabs" :key="tab.key">
              <div class="image-picker">
                <div
                  class="image-stage-wrap mb-3"
                  :class="`image-stage-wrap--${tab.key}`"
                >
                  <div
                    class="image-stage rounded"
                    :class="`image-stage--${tab.key}`"
                    :style="stageStyle(tab)"
                  >
                    <div
                      v-if="!selectedUrl(tab.key)"
                      class="d-flex fill-height align-center justify-center"
                    >
                      <span class="text-caption text--secondary">
                        Sem imagem para {{ tab.label.toLowerCase() }}
                      </span>
                    </div>
                  </div>

                  <div
                    v-if="selectedItem(tab.key)"
                    class="stage-meta d-flex align-center justify-center flex-wrap"
                    style="gap: 6px"
                  >
                    <v-chip
                      x-small
                      label
                      :color="sourceChipColor(selectedItem(tab.key))"
                      text-color="white"
                    >
                      {{ sourceLabel(selectedItem(tab.key)) }}
                    </v-chip>
                    <v-btn
                      v-if="selectedItem(tab.key).sourceUrl"
                      icon
                      x-small
                      :href="selectedItem(tab.key).sourceUrl"
                      target="_blank"
                      rel="noopener"
                    >
                      <v-icon x-small>mdi-open-in-new</v-icon>
                    </v-btn>
                  </div>
                </div>

                <div
                  v-if="optionsFor(tab.key).length"
                  class="d-flex align-center flex-wrap mb-3"
                  style="gap: 8px"
                >
                  <v-btn
                    small
                    color="primary"
                    outlined
                    :loading="adapting && adaptingFormat === tab.key"
                    :disabled="
                      adapting || loading || !selectedItem(tab.key)?.image
                    "
                    @click="adaptSelected(tab.key)"
                  >
                    <v-icon left small>mdi-auto-fix</v-icon>
                    Ajustar com IA ({{ tab.ratio }})
                  </v-btn>
                  <span class="text-caption text--secondary">
                    Gera uma versão no formato correto a partir da imagem
                    selecionada
                  </span>
                </div>

                <div v-if="optionsFor(tab.key).length" class="thumb-strip">
                  <button
                    v-for="(item, index) in optionsFor(tab.key)"
                    :key="`${tab.key}-${index}-${item.source || 'x'}`"
                    type="button"
                    class="thumb-btn"
                    :class="{
                      'thumb-btn--active': index === indexFor(tab.key),
                      [`thumb-btn--${tab.key}`]: true,
                    }"
                    :style="thumbStyle(item)"
                    :title="sourceLabel(item)"
                    :disabled="adapting"
                    @click="setIndex(tab.key, index)"
                  >
                    <span class="thumb-index">{{ index + 1 }}</span>
                  </button>
                </div>
                <p v-else class="text-caption text--secondary mb-0">
                  Nenhuma opção encontrada para este formato.
                </p>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </template>

        <v-alert v-if="error" type="error" dense text class="mt-3 mb-0">
          {{ error }}
        </v-alert>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-btn text :disabled="loading || adapting" @click="close">
          Cancelar
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="suggestion"
          text
          color="primary"
          :disabled="loading || adapting"
          @click="regenerate"
        >
          Gerar novamente
        </v-btn>
        <v-btn
          v-if="suggestion"
          color="success"
          :disabled="loading || adapting"
          @click="accept"
        >
          Usar sugestão
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import registrationsService from "@/services/admin/registrations";
import ImageConverter from "@/utils/ImageConverter";

function toDataUrl(item) {
  if (!item?.image) return null;
  return `data:${item.mimeType || "image/jpeg"};base64,${item.image}`;
}

const IMAGE_ARRAY_KEY = {
  cover: "coverPhoto",
  profile: "profilePhoto",
  vertical: "verticalCoverPhoto",
};

// Resoluções ~1K da API Gemini por aspect ratio
const ADAPT_COMPRESS_SETTINGS = {
  profile: {
    maxWidth: 1024,
    maxHeight: 1024,
    outputType: "image/webp",
    quality: 0.92,
  },
  cover: {
    maxWidth: 1344,
    maxHeight: 768,
    outputType: "image/webp",
    quality: 0.92,
  },
  vertical: {
    maxWidth: 768,
    maxHeight: 1344,
    outputType: "image/webp",
    quality: 0.92,
  },
};

export default {
  name: "GenerateArtistDialog",
  data() {
    return {
      dialog: false,
      name: "",
      loading: false,
      adapting: false,
      adaptingFormat: null,
      error: null,
      suggestion: null,
      imageTab: 0,
      profileIndex: 0,
      coverIndex: 0,
      verticalIndex: 0,
    };
  },
  computed: {
    profileOptions() {
      return this.suggestion?.images?.profilePhoto || [];
    },
    coverOptions() {
      return this.suggestion?.images?.coverPhoto || [];
    },
    verticalOptions() {
      return this.suggestion?.images?.verticalCoverPhoto || [];
    },
    imageTabs() {
      return [
        {
          key: "cover",
          label: "Cover",
          ratio: "2:1",
          count: this.coverOptions.length,
        },
        {
          key: "profile",
          label: "Perfil",
          ratio: "1:1",
          count: this.profileOptions.length,
        },
        {
          key: "vertical",
          label: "Vertical",
          ratio: "9:16",
          count: this.verticalOptions.length,
        },
      ];
    },
  },
  methods: {
    optionsFor(key) {
      if (key === "cover") return this.coverOptions;
      if (key === "profile") return this.profileOptions;
      return this.verticalOptions;
    },
    indexFor(key) {
      if (key === "cover") return this.coverIndex;
      if (key === "profile") return this.profileIndex;
      return this.verticalIndex;
    },
    setIndex(key, index) {
      if (key === "cover") this.coverIndex = index;
      else if (key === "profile") this.profileIndex = index;
      else this.verticalIndex = index;
    },
    selectedItem(key) {
      return this.optionsFor(key)[this.indexFor(key)] || null;
    },
    selectedUrl(key) {
      return toDataUrl(this.selectedItem(key));
    },
    stageStyle(tab) {
      const url = this.selectedUrl(tab.key);
      return {
        backgroundImage: url ? `url(${url})` : "none",
      };
    },
    thumbStyle(item) {
      const url = toDataUrl(item);
      return {
        backgroundImage: url ? `url(${url})` : "none",
      };
    },
    sourceLabel(item) {
      if (!item) return "";
      if (item.sourceLabel) return item.sourceLabel;
      if (item.source === "adapted") return "Ajustada com IA";
      if (item.source === "generated") return "Gerada com IA";
      return "Resultado da web";
    },
    sourceChipColor(item) {
      const source = item?.source;
      if (source === "adapted") return "primary";
      if (source === "generated") return "deep-purple";
      if (item?.sourceLabel === "Spotify") return "green darken-1";
      if (item?.sourceLabel === "YouTube Music") return "red darken-1";
      return "blue-grey";
    },
    open({ name } = {}) {
      this.name = name || "";
      this.suggestion = null;
      this.error = null;
      this.loading = false;
      this.adapting = false;
      this.adaptingFormat = null;
      this.imageTab = 0;
      this.profileIndex = 0;
      this.coverIndex = 0;
      this.verticalIndex = 0;
      this.dialog = true;
    },
    close() {
      if (this.loading || this.adapting) return;
      this.dialog = false;
      this.suggestion = null;
      this.error = null;
    },
    async generate() {
      if (!(this.name || "").trim()) {
        this.error = "Informe o nome do artista";
        return;
      }
      this.loading = true;
      this.error = null;
      this.suggestion = null;
      try {
        const response = await registrationsService.generateArtist({
          name: this.name.trim(),
        });
        this.suggestion = response.artist;
        this.imageTab = 0;
        this.profileIndex = 0;
        this.coverIndex = 0;
        this.verticalIndex = 0;
        if (this.suggestion?.name) this.name = this.suggestion.name;
      } catch (error) {
        this.error = error.message || "Erro ao gerar sugestão do artista";
      } finally {
        this.loading = false;
      }
    },
    regenerate() {
      this.suggestion = null;
      this.generate();
    },
    async adaptSelected(format) {
      const selected = this.selectedItem(format);
      if (!selected?.image || !this.suggestion) return;

      this.adapting = true;
      this.adaptingFormat = format;
      this.error = null;

      try {
        const imageConverter = new ImageConverter();
        const compressed = await imageConverter.compressBase64(
          selected.image,
          selected.mimeType || "image/jpeg",
          ADAPT_COMPRESS_SETTINGS[format] || ADAPT_COMPRESS_SETTINGS.profile
        );

        const response = await registrationsService.adaptArtistImage({
          image: compressed.image,
          mimeType: compressed.mimeType,
          format,
          artistName: this.suggestion.name,
          sourceUrl: selected.sourceUrl || null,
        });

        const adapted = response.image;
        if (!adapted?.image) {
          throw { message: "Resposta inválida ao ajustar imagem" };
        }

        const arrayKey = IMAGE_ARRAY_KEY[format];
        if (!this.suggestion.images) {
          this.$set(this.suggestion, "images", {
            profilePhoto: [],
            coverPhoto: [],
            verticalCoverPhoto: [],
          });
        }
        if (!Array.isArray(this.suggestion.images[arrayKey])) {
          this.$set(this.suggestion.images, arrayKey, []);
        }

        this.suggestion.images[arrayKey].push(adapted);
        this.setIndex(format, this.suggestion.images[arrayKey].length - 1);
      } catch (error) {
        this.error = error.message || "Erro ao ajustar imagem com IA";
      } finally {
        this.adapting = false;
        this.adaptingFormat = null;
      }
    },
    accept() {
      if (!this.suggestion) return;
      this.$emit("confirm", {
        name: this.suggestion.name,
        description: this.suggestion.description,
        shortDescription: this.suggestion.shortDescription,
        slug: this.suggestion.slug,
        category: this.suggestion.category,
        color: this.suggestion.color,
        spotifyId: this.suggestion.spotifyId,
        profilePhoto: this.profileOptions[this.profileIndex] || null,
        coverPhoto: this.coverOptions[this.coverIndex] || null,
        verticalCoverPhoto: this.verticalOptions[this.verticalIndex] || null,
      });
      this.dialog = false;
      this.suggestion = null;
    },
  },
};
</script>

<style scoped>
.artist-summary {
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.summary-bio {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.spotify-mini {
  flex-shrink: 0;
  padding: 6px 10px;
  border-radius: 8px;
  background: rgba(29, 185, 84, 0.08);
}

.image-stage-wrap {
  width: 100%;
}

.image-stage-wrap--profile,
.image-stage-wrap--vertical {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.image-stage {
  position: relative;
  width: 100%;
  background-color: #e8e8e8;
  background-size: cover;
  background-position: center;
  overflow: hidden;
}

.image-stage--cover {
  aspect-ratio: 2 / 1;
  max-height: 280px;
}

.image-stage--profile {
  aspect-ratio: 1 / 1;
  width: 100%;
  max-width: 280px;
  border-radius: 50% !important;
}

.image-stage--vertical {
  aspect-ratio: 9 / 16;
  width: 100%;
  max-width: 220px;
  max-height: 360px;
}

.stage-meta {
  margin-top: 10px;
}

.thumb-strip {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.thumb-btn {
  position: relative;
  flex: 0 0 auto;
  border: 2px solid transparent;
  background-color: #e0e0e0;
  background-size: cover;
  background-position: center;
  cursor: pointer;
  padding: 0;
  outline: none;
}

.thumb-btn:disabled {
  cursor: wait;
  opacity: 0.7;
}

.thumb-btn--cover {
  width: 96px;
  height: 48px;
  border-radius: 8px;
}

.thumb-btn--profile {
  width: 56px;
  height: 56px;
  border-radius: 50%;
}

.thumb-btn--vertical {
  width: 48px;
  height: 84px;
  border-radius: 8px;
}

.thumb-btn--active {
  border-color: var(--v-primary-base, #1976d2);
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

.thumb-index {
  position: absolute;
  top: 4px;
  left: 4px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 10px;
  line-height: 16px;
  text-align: center;
}

.theme--dark .artist-summary {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.08);
}
</style>
